import { supabase } from '../utils/supabase';

/**
 * Vendor Data Service
 * Handles all vendor-related data operations with Supabase
 * Separated from vendorService.js (inquiry service) for better organization
 */
class VendorDataService {
  /**
   * Get all vendors with optional filters and pagination
   * @param {Object} filters - Filter options
   * @returns {Promise<Object>} Result with vendors data
   */
  async getVendors(filters = {}) {
    try {
      let query = supabase
        .from('vendor_profiles')
        .select(`
          *,
          vendor_gallery!vendor_id (
            id,
            image_url,
            title,
            is_featured,
            sort_order
          ),
          vendor_packages!vendor_id (
            id,
            name,
            description,
            price,
            duration,
            features,
            is_active
          )
        `, { count: 'exact' });

      // Apply filters
      if (filters.category) {
        query = query.eq('category', filters.category);
      }

      if (filters.city) {
        query = query.eq('city', filters.city);
      }

      if (filters.state) {
        query = query.eq('state', filters.state);
      }

      if (filters.isVerified !== undefined) {
        query = query.eq('is_verified', filters.isVerified);
      }

      if (filters.isFeatured !== undefined) {
        query = query.eq('is_featured', filters.isFeatured);
      }

      if (filters.minPrice) {
        query = query.gte('starting_price', filters.minPrice);
      }

      if (filters.maxPrice) {
        query = query.lte('starting_price', filters.maxPrice);
      }

      if (filters.culturalSpecialties && filters.culturalSpecialties.length > 0) {
        query = query.contains('cultural_specialties', filters.culturalSpecialties);
      }

      if (filters.services && filters.services.length > 0) {
        query = query.contains('services', filters.services);
      }

      if (filters.subscriptionType) {
        query = query.eq('subscription_type', filters.subscriptionType);
      }

      // Apply sorting
      const sortBy = filters.sortBy || 'created_at';
      const sortOrder = filters.sortOrder || 'desc';
      query = query.order(sortBy, { ascending: sortOrder === 'asc' });

      // Apply pagination
      const page = filters.page || 1;
      const limit = filters.limit || 20;
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) {
        console.error('Error fetching vendors:', error);
        return { success: false, error: error.message };
      }

      return {
        success: true,
        data: data || [],
        count,
        page,
        totalPages: Math.ceil(count / limit)
      };
    } catch (error) {
      console.error('Error in getVendors:', error);
      if (error?.message?.includes('Failed to fetch')) {
        return {
          success: false,
          error: 'Cannot connect to database. Please check your connection.'
        };
      }
      return { success: false, error: 'Failed to fetch vendors' };
    }
  }

  /**
   * Get featured vendors for homepage
   * @param {number} limit - Number of vendors to return
   * @returns {Promise<Object>} Result with featured vendors
   */
  async getFeaturedVendors(limit = 10) {
    try {
      const { data, error } = await supabase
        .from('vendor_profiles')
        .select(`
          *,
          vendor_gallery!vendor_id (
            id,
            image_url,
            title,
            is_featured,
            sort_order
          )
        `)
        .eq('is_featured', true)
        .eq('is_verified', true)
        .order('total_bookings', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching featured vendors:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error in getFeaturedVendors:', error);
      return { success: false, error: 'Failed to fetch featured vendors' };
    }
  }

  /**
   * Get trending vendors
   * @param {number} limit - Number of vendors to return
   * @returns {Promise<Object>} Result with trending vendors
   */
  async getTrendingVendors(limit = 10) {
    try {
      // Get vendors with most recent bookings
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data, error } = await supabase
        .from('vendor_profiles')
        .select(`
          *,
          vendor_gallery!vendor_id (
            id,
            image_url,
            title,
            is_featured,
            sort_order
          )
        `)
        .eq('is_verified', true)
        .order('total_bookings', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching trending vendors:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error in getTrendingVendors:', error);
      return { success: false, error: 'Failed to fetch trending vendors' };
    }
  }

  /**
   * Get vendor by ID with full details
   * @param {string} vendorId - Vendor UUID
   * @returns {Promise<Object>} Result with vendor data
   */
  async getVendorById(vendorId) {
    try {
      const { data, error } = await supabase
        .from('vendor_profiles')
        .select(`
          *,
          vendor_gallery!vendor_id (
            id,
            image_url,
            title,
            description,
            is_featured,
            sort_order
          ),
          vendor_packages!vendor_id (
            id,
            name,
            description,
            price,
            duration,
            features,
            is_active,
            sort_order
          ),
          reviews!vendor_id (
            id,
            rating,
            title,
            comment,
            review_images,
            is_verified,
            helpful_count,
            created_at,
            user_profiles!user_id (
              id,
              full_name,
              profile_image
            )
          )
        `)
        .eq('id', vendorId)
        .single();

      if (error) {
        console.error('Error fetching vendor by ID:', error);
        return { success: false, error: error.message };
      }

      if (!data) {
        return { success: false, error: 'Vendor not found' };
      }

      // Calculate average rating from reviews
      if (data.reviews && data.reviews.length > 0) {
        const totalRating = data.reviews.reduce((sum, review) => sum + review.rating, 0);
        data.averageRating = (totalRating / data.reviews.length).toFixed(1);
        data.reviewCount = data.reviews.length;
      } else {
        data.averageRating = 0;
        data.reviewCount = 0;
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in getVendorById:', error);
      return { success: false, error: 'Failed to fetch vendor details' };
    }
  }

  /**
   * Get vendor by slug (user-friendly URL)
   * Creates slug from business_name if needed
   * @param {string} slug - URL-friendly identifier
   * @returns {Promise<Object>} Result with vendor data
   */
  async getVendorBySlug(slug) {
    try {
      // First, try to find by exact business_name match
      // Convert slug back to readable name (e.g., "rajesh-photography" -> "Rajesh Photography")
      const businessName = slug
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      const { data, error } = await supabase
        .from('vendor_profiles')
        .select(`
          *,
          vendor_gallery!vendor_id (
            id,
            image_url,
            title,
            description,
            is_featured,
            sort_order
          ),
          vendor_packages!vendor_id (
            id,
            name,
            description,
            price,
            duration,
            features,
            is_active,
            sort_order
          ),
          reviews!vendor_id (
            id,
            rating,
            title,
            comment,
            review_images,
            is_verified,
            helpful_count,
            created_at,
            user_profiles!user_id (
              id,
              full_name,
              profile_image
            )
          )
        `)
        .ilike('business_name', businessName)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching vendor by slug:', error);
        return { success: false, error: error.message };
      }

      if (!data) {
        return { success: false, error: 'Vendor not found' };
      }

      // Calculate average rating from reviews
      if (data.reviews && data.reviews.length > 0) {
        const totalRating = data.reviews.reduce((sum, review) => sum + review.rating, 0);
        data.averageRating = (totalRating / data.reviews.length).toFixed(1);
        data.reviewCount = data.reviews.length;
      } else {
        data.averageRating = 0;
        data.reviewCount = 0;
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in getVendorById:', error);
      return { success: false, error: 'Failed to fetch vendor details' };
    }
  }

  /**
   * Get vendor profile by user ID
   * @param {string} userId - User UUID
   * @returns {Promise<Object>} Result with vendor data
   */
  async getVendorByUserId(userId) {
    try {
      const { data, error } = await supabase
        .from('vendor_profiles')
        .select(`
          *,
          vendor_gallery!vendor_id (
            id,
            image_url,
            title,
            description,
            is_featured,
            sort_order
          ),
          vendor_packages!vendor_id (
            id,
            name,
            description,
            price,
            duration,
            features,
            is_active,
            sort_order
          )
        `)
        .eq('user_id', userId)
        .single();

      if (error) {
        // User might not have a vendor profile
        if (error.code === 'PGRST116') {
          return { success: false, error: 'No vendor profile found for this user' };
        }
        console.error('Error fetching vendor by user ID:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in getVendorByUserId:', error);
      return { success: false, error: 'Failed to fetch vendor profile' };
    }
  }

  /**
   * Get vendors by category
   * @param {string} searchTerm - Search query
   * @param {Object} filters - Additional filters
   * @returns {Promise<Object>} Result with matching vendors
   */
  async searchVendors(searchTerm, filters = {}) {
    try {
      let query = supabase
        .from('vendor_profiles')
        .select(`
          *,
          vendor_gallery!vendor_id (
            id,
            image_url,
            is_featured
          )
        `, { count: 'exact' });

      // Full-text search across multiple fields
      if (searchTerm && searchTerm.trim()) {
        const searchPattern = `%${searchTerm}%`;
        query = query.or(
          `business_name.ilike.${searchPattern},` +
          `description.ilike.${searchPattern},` +
          `category.ilike.${searchPattern},` +
          `city.ilike.${searchPattern},` +
          `state.ilike.${searchPattern}`
        );
      }

      // Apply additional filters
      if (filters.category) {
        query = query.eq('category', filters.category);
      }

      if (filters.city) {
        query = query.eq('city', filters.city);
      }

      // Apply pagination
      const limit = filters.limit || 20;
      query = query.limit(limit);

      const { data, error, count } = await query;

      if (error) {
        console.error('Error searching vendors:', error);
        return { success: false, error: error.message };
      }

      return {
        success: true,
        data: data || [],
        count
      };
    } catch (error) {
      console.error('Error in searchVendors:', error);
      return { success: false, error: 'Failed to search vendors' };
    }
  }

  /**
   * Get vendors by category
   * @param {string} category - Vendor category
   * @param {Object} filters - Additional filters
   * @returns {Promise<Object>} Result with vendors in category
   */
  async getVendorsByCategory(category, filters = {}) {
    return this.getVendors({ ...filters, category });
  }

  /**
   * Get vendors by location
   * @param {string} city - City name
   * @param {string} state - State code
   * @param {Object} filters - Additional filters
   * @returns {Promise<Object>} Result with vendors in location
   */
  async getVendorsByLocation(city, state, filters = {}) {
    return this.getVendors({ ...filters, city, state });
  }

  /**
   * Create new vendor profile
   * @param {Object} vendorData - Vendor profile data
   * @returns {Promise<Object>} Result with created vendor
   */
  async createVendor(vendorData) {
    try {
      const { data, error } = await supabase
        .from('vendor_profiles')
        .insert({
          ...vendorData,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating vendor:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in createVendor:', error);
      return { success: false, error: 'Failed to create vendor profile' };
    }
  }

  /**
   * Update vendor profile
   * @param {string} vendorId - Vendor UUID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Result with updated vendor
   */
  async updateVendor(vendorId, updates) {
    try {
      const { data, error } = await supabase
        .from('vendor_profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', vendorId)
        .select()
        .single();

      if (error) {
        console.error('Error updating vendor:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in updateVendor:', error);
      return { success: false, error: 'Failed to update vendor profile' };
    }
  }

  /**
   * Delete vendor profile
   * @param {string} vendorId - Vendor UUID
   * @returns {Promise<Object>} Result of deletion
   */
  async deleteVendor(vendorId) {
    try {
      const { error } = await supabase
        .from('vendor_profiles')
        .delete()
        .eq('id', vendorId);

      if (error) {
        console.error('Error deleting vendor:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in deleteVendor:', error);
      return { success: false, error: 'Failed to delete vendor profile' };
    }
  }

  /**
   * Get vendor analytics/statistics
   * @param {string} vendorId - Vendor UUID
   * @returns {Promise<Object>} Result with vendor analytics
   */
  async getVendorAnalytics(vendorId) {
    try {
      // Fetch all related data in parallel
      const [bookingsResult, inquiriesResult, reviewsResult, profileResult, viewsResult] = await Promise.all([
        supabase
          .from('bookings')
          .select('id, total_amount, status, created_at', { count: 'exact' })
          .eq('vendor_id', vendorId),
        supabase
          .from('inquiries')
          .select('id, status, created_at', { count: 'exact' })
          .eq('vendor_id', vendorId),
        supabase
          .from('reviews')
          .select('id, rating, created_at')
          .eq('vendor_id', vendorId),
        supabase
          .from('vendor_profiles')
          .select('total_bookings, response_time, completion_rate, created_at')
          .eq('id', vendorId)
          .single(),
        supabase
          .from('vendor_views')
          .select('id, created_at', { count: 'exact' })
          .eq('vendor_id', vendorId)
      ]);

      const bookings = bookingsResult.data || [];
      const inquiries = inquiriesResult.data || [];
      const reviews = reviewsResult.data || [];
      const profile = profileResult.data;
      const views = viewsResult.data || [];

      // Calculate analytics
      const totalRevenue = bookings
        .filter(b => b.status === 'confirmed' || b.status === 'completed')
        .reduce((sum, b) => sum + (b.total_amount || 0), 0);

      const averageRating = reviews.length > 0
        ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
        : 0;

      // Calculate growth metrics (compare last 30 days vs previous 30 days)
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);

      // Bookings growth
      const recentBookings = bookings.filter(b => new Date(b.created_at) >= thirtyDaysAgo).length;
      const previousBookings = bookings.filter(b =>
        new Date(b.created_at) >= sixtyDaysAgo && new Date(b.created_at) < thirtyDaysAgo
      ).length;
      const bookingsGrowth = previousBookings > 0
        ? ((recentBookings - previousBookings) / previousBookings) * 100
        : 0;

      // Profile views growth
      const recentViews = views.filter(v => new Date(v.created_at) >= thirtyDaysAgo).length;
      const previousViews = views.filter(v =>
        new Date(v.created_at) >= sixtyDaysAgo && new Date(v.created_at) < thirtyDaysAgo
      ).length;
      const profileViewsGrowth = previousViews > 0
        ? ((recentViews - previousViews) / previousViews) * 100
        : 0;

      // Inquiries growth
      const recentInquiries = inquiries.filter(i => new Date(i.created_at) >= thirtyDaysAgo).length;
      const previousInquiries = inquiries.filter(i =>
        new Date(i.created_at) >= sixtyDaysAgo && new Date(i.created_at) < thirtyDaysAgo
      ).length;
      const inquiriesGrowth = previousInquiries > 0
        ? ((recentInquiries - previousInquiries) / previousInquiries) * 100
        : 0;

      // Revenue growth
      const recentRevenue = bookings
        .filter(b => (b.status === 'confirmed' || b.status === 'completed') && new Date(b.created_at) >= thirtyDaysAgo)
        .reduce((sum, b) => sum + (b.total_amount || 0), 0);
      const previousRevenue = bookings
        .filter(b => (b.status === 'confirmed' || b.status === 'completed') &&
          new Date(b.created_at) >= sixtyDaysAgo && new Date(b.created_at) < thirtyDaysAgo)
        .reduce((sum, b) => sum + (b.total_amount || 0), 0);
      const revenueGrowth = previousRevenue > 0
        ? ((recentRevenue - previousRevenue) / previousRevenue) * 100
        : 0;

      // Response rate calculation
      const respondedInquiries = inquiries.filter(i => i.status !== 'pending').length;
      const responseRate = inquiries.length > 0
        ? ((respondedInquiries / inquiries.length) * 100)
        : 0;

      return {
        success: true,
        data: {
          // Totals
          totalBookings: bookingsResult.count || 0,
          totalInquiries: inquiriesResult.count || 0,
          totalReviews: reviews.length,
          averageRating: parseFloat(averageRating.toFixed(1)),
          totalRevenue,

          // Profile views
          profileViews: viewsResult.count || 0,
          profileViewsGrowth: parseFloat(profileViewsGrowth.toFixed(1)),

          // Inquiries
          inquiries: inquiriesResult.count || 0,
          inquiriesGrowth: parseFloat(inquiriesGrowth.toFixed(1)),
          pendingInquiries: inquiries.filter(i => i.status === 'pending').length,

          // Bookings
          bookings: bookingsResult.count || 0,
          bookingsGrowth: parseFloat(bookingsGrowth.toFixed(1)),
          recentBookings,

          // Revenue
          revenue: totalRevenue,
          revenueGrowth: parseFloat(revenueGrowth.toFixed(1)),
          monthlyRevenue: recentRevenue,

          // Performance metrics
          responseRate: parseFloat(responseRate.toFixed(1)),
          responseTime: profile?.response_time || 'N/A',
          completionRate: profile?.completion_rate || 0,
          conversionRate: inquiries.length > 0
            ? parseFloat(((bookings.length / inquiries.length) * 100).toFixed(1))
            : 0
        }
      };
    } catch (error) {
      console.error('Error in getVendorAnalytics:', error);
      return { success: false, error: 'Failed to fetch vendor analytics' };
    }
  }

  /**
   * Calculate search ranking for a vendor
   * Based on multiple factors: completeness, reviews, bookings, views, response rate
   * @param {string} vendorId - Vendor ID
   * @returns {Promise<Object>} Result with ranking data
   */
  async calculateSearchRanking(vendorId) {
    try {
      // Get vendor profile
      const { data: vendor, error: vendorError } = await supabase
        .from('vendor_profiles')
        .select('*')
        .eq('id', vendorId)
        .single();

      if (vendorError) throw vendorError;

      // Get analytics data
      const analytics = await this.getVendorAnalytics(vendorId);
      if (!analytics.success) throw new Error('Failed to get analytics');

      const { data: analyticsData } = analytics;

      // Calculate profile completeness (0-100)
      let completeness = 0;
      const fields = [
        vendor.business_name,
        vendor.description,
        vendor.phone,
        vendor.email,
        vendor.city,
        vendor.state,
        vendor.category,
        vendor.starting_price,
        vendor.services?.length > 0,
        vendor.cultural_specialties?.length > 0
      ];
      completeness = (fields.filter(Boolean).length / fields.length) * 100;

      // Calculate ranking score (0-100)
      let rankingScore = 0;

      // Profile completeness (30 points)
      rankingScore += (completeness / 100) * 30;

      // Reviews and ratings (25 points)
      const avgRating = analyticsData.averageRating || 0;
      const reviewCount = analyticsData.totalReviews || 0;
      rankingScore += (avgRating / 5) * 15; // 15 points for rating
      rankingScore += Math.min((reviewCount / 50) * 10, 10); // 10 points for review count (cap at 50 reviews)

      // Bookings (20 points)
      const bookings = analyticsData.totalBookings || 0;
      rankingScore += Math.min((bookings / 20) * 20, 20); // Cap at 20 bookings

      // Profile views (15 points)
      const views = analyticsData.profileViews || 0;
      rankingScore += Math.min((views / 500) * 15, 15); // Cap at 500 views

      // Response rate (10 points)
      const responseRate = analyticsData.responseRate || 0;
      rankingScore += (responseRate / 100) * 10;

      // Bonuses
      if (vendor.is_verified) rankingScore += 5;
      if (vendor.is_featured) rankingScore += 3;

      // Cap at 100
      rankingScore = Math.min(rankingScore, 100);

      // Get category ranking
      const { data: categoryVendors, error: categoryError } = await supabase
        .from('vendor_profiles')
        .select('id')
        .eq('category', vendor.category);

      if (categoryError) throw categoryError;

      const totalInCategory = categoryVendors?.length || 1;

      // Estimate position (this is simplified - real implementation would rank all vendors)
      const estimatedPosition = Math.ceil((100 - rankingScore) / 100 * totalInCategory);
      const percentile = ((totalInCategory - estimatedPosition) / totalInCategory) * 100;

      return {
        success: true,
        data: {
          rankingScore: parseFloat(rankingScore.toFixed(1)),
          estimatedPosition,
          totalInCategory,
          percentile: parseFloat(percentile.toFixed(1)),
          factors: {
            profileCompleteness: parseFloat(completeness.toFixed(1)),
            averageRating: avgRating,
            totalReviews: reviewCount,
            totalBookings: bookings,
            profileViews: views,
            responseRate: parseFloat(responseRate.toFixed(1)),
            isVerified: vendor.is_verified,
            isFeatured: vendor.is_featured
          }
        }
      };
    } catch (error) {
      console.error('Error calculating search ranking:', error);
      return { success: false, error: 'Failed to calculate search ranking' };
    }
  }

  /**
   * Get all available vendor categories
   * @returns {Promise<Object>} Result with unique categories
   */
  async getCategories() {
    try {
      const { data, error } = await supabase
        .from('vendor_profiles')
        .select('category')
        .not('category', 'is', null);

      if (error) {
        console.error('Error fetching categories:', error);
        return { success: false, error: error.message };
      }

      // Get unique categories with counts
      const categoryMap = {};
      data.forEach(item => {
        if (item.category) {
          categoryMap[item.category] = (categoryMap[item.category] || 0) + 1;
        }
      });

      const categories = Object.entries(categoryMap).map(([name, count]) => ({
        name,
        count,
        slug: name.toLowerCase().replace(/\s+/g, '-')
      }));

      return { success: true, data: categories };
    } catch (error) {
      console.error('Error in getCategories:', error);
      return { success: false, error: 'Failed to fetch categories' };
    }
  }

  /**
   * Subscribe to real-time vendor updates
   * @param {string} vendorId - Vendor UUID
   * @param {Function} callback - Callback function for updates
   * @returns {Object} Subscription object
   */
  subscribeToVendorUpdates(vendorId, callback) {
    const subscription = supabase
      .channel(`vendor_updates_${vendorId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'vendor_profiles',
          filter: `id=eq.${vendorId}`
        },
        callback
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bookings',
          filter: `vendor_id=eq.${vendorId}`
        },
        callback
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'inquiries',
          filter: `vendor_id=eq.${vendorId}`
        },
        callback
      )
      .subscribe();

    return subscription;
  }

  /**
   * Unsubscribe from real-time updates
   * @param {Object} subscription - Subscription object
   */
  unsubscribeFromUpdates(subscription) {
    if (subscription) {
      supabase.removeChannel(subscription);
    }
  }
}

// Export singleton instance
const vendorDataService = new VendorDataService();
export default vendorDataService;
